import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type Theme = 'light' | 'dark'
export type ThemeMode = 'light' | 'dark' | 'system'

export interface GroupTheme {
  id: string
  name: string
  primary_color: string
  secondary_color: string
  accent_color: string
  is_active: boolean
  created_at: string
  updated_at: string
}

interface ThemeContextType {
  /** Resolved appearance applied to the document */
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  /** Preference including system follow */
  themeMode: ThemeMode
  setThemeMode: (mode: ThemeMode) => void
  isDarkMode: boolean
  systemPrefersDark: boolean
  currentGroupTheme: GroupTheme
  setGroupTheme: (theme: GroupTheme) => void
  applyGroupTheme: (theme: GroupTheme) => void
  resetGroupTheme: () => void
}

const DEFAULT_GROUP_THEME: GroupTheme = {
  id: 'default',
  name: 'Default',
  primary_color: '#0B2345',
  secondary_color: '#071A33',
  accent_color: '#F2C14E',
  is_active: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

function resolveTheme(mode: ThemeMode, systemPrefersDark: boolean): Theme {
  if (mode === 'system') return systemPrefersDark ? 'dark' : 'light'
  return mode
}

function readInitialThemeMode(): ThemeMode {
  // Cinematic redesign is dark-only until light theme is fully supported.
  return 'dark'
}

function applyDocumentTheme(theme: Theme) {
  const root = document.documentElement
  root.classList.remove('light', 'dark', 'modern')
  root.classList.add(theme)
  document.body.classList.remove('modern')
}

function applyGroupThemeVars(theme: GroupTheme) {
  const root = document.documentElement
  root.style.setProperty('--color-primary', theme.primary_color)
  root.style.setProperty('--color-secondary', theme.secondary_color)
  root.style.setProperty('--color-accent', theme.accent_color)
}

/**
 * Single web theme provider — light/dark/system + optional group accent vars.
 * Mounted once in App.tsx. Do not add a second theme provider.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => readInitialThemeMode())
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => readSystemPrefersDark())
  const [currentGroupTheme, setCurrentGroupTheme] = useState<GroupTheme>(() => {
    try {
      const saved = localStorage.getItem('groupTheme')
      if (saved) return JSON.parse(saved) as GroupTheme
    } catch {
      localStorage.removeItem('groupTheme')
    }
    return DEFAULT_GROUP_THEME
  })

  const theme = useMemo((): Theme => 'dark', [])

  useEffect(() => {
    applyDocumentTheme(theme)
    localStorage.setItem('theme', theme)
    localStorage.setItem('themeMode', themeMode)
    localStorage.removeItem('darkMode')
  }, [theme, themeMode])

  useEffect(() => {
    applyGroupThemeVars(currentGroupTheme)
  }, [currentGroupTheme])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches)
    }
    setSystemPrefersDark(mediaQuery.matches)
    mediaQuery.addEventListener('change', onChange)
    return () => mediaQuery.removeEventListener('change', onChange)
  }, [])

  const setTheme = useCallback((next: Theme) => {
    setThemeModeState(next)
  }, [])

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeModeState('dark')
  }, [])

  const applyGroupTheme = useCallback((groupTheme: GroupTheme) => {
    setCurrentGroupTheme(groupTheme)
    applyGroupThemeVars(groupTheme)
    localStorage.setItem('groupTheme', JSON.stringify(groupTheme))
  }, [])

  const setGroupTheme = useCallback(
    (groupTheme: GroupTheme) => {
      applyGroupTheme(groupTheme)
    },
    [applyGroupTheme]
  )

  const resetGroupTheme = useCallback(() => {
    applyGroupTheme(DEFAULT_GROUP_THEME)
    localStorage.removeItem('groupTheme')
  }, [applyGroupTheme])

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
    themeMode,
    setThemeMode,
    isDarkMode: theme === 'dark',
    systemPrefersDark,
    currentGroupTheme,
    setGroupTheme,
    applyGroupTheme,
    resetGroupTheme,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/** @deprecated Use useTheme — alias for UnifiedThemeContext consumers */
export function useUnifiedTheme() {
  const ctx = useTheme()
  return {
    isDarkMode: ctx.isDarkMode,
    themeMode: ctx.themeMode,
    toggleDarkMode: ctx.toggleTheme,
    setThemeMode: ctx.setThemeMode,
    currentGroupTheme: ctx.currentGroupTheme,
    setGroupTheme: ctx.setGroupTheme,
    applyGroupTheme: ctx.applyGroupTheme,
    resetGroupTheme: ctx.resetGroupTheme,
    systemPrefersDark: ctx.systemPrefersDark,
    getThemeColor: (colorPath: string) => {
      const map: Record<string, string> = {
        primary: ctx.currentGroupTheme.primary_color,
        secondary: ctx.currentGroupTheme.secondary_color,
        accent: ctx.currentGroupTheme.accent_color,
      }
      return map[colorPath] || ctx.currentGroupTheme.primary_color
    },
    getContrastColor: (backgroundColor: string) => {
      const hex = backgroundColor.replace('#', '')
      if (hex.length < 6) return '#ffffff'
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5 ? '#000000' : '#ffffff'
    },
  }
}
